'use client';
import { useRef } from 'react';
import { useInView } from 'react-intersection-observer';

import { SubHeadline, Text } from '~components/Typography';
import { routes } from '~constants/routes';
import { useFormatBotStatistics } from '~entities/bot/hooks/useFormatBotStatistics';
import { useTranslations } from '~i18n/hooks';
import LockCircle from '~public/assets/icons/lock-circle.svg';
import Messages from '~public/assets/icons/messages.svg';
import MessagesIcon from '~public/assets/icons/messagesv2.svg';
import TickSimple from '~public/assets/icons/tick-simple.svg';
import { CreatorLink } from '~shared/ui/molecules/CreatorLink';
import { GridCarousel } from '~shared/ui/organisms/GridCarousel';
import { formatBotPaymentOptions } from '~shared/utils/payments';

import { useComputeActiveDistance } from '../../hooks/useComputeActiveDistance';
import type { BotGridItem } from '../../types';
import { Footer, Images, Info, Tag, Tags, Video, Wrapper } from '../styles';
import {
	Description,
	MessagesButton,
	StyledGridItem,
	Title,
	TotalMessages,
} from './styles';

type Props = {
	item: BotGridItem;
	index: number;
	userLevel: number;
	animated: boolean;
	mobileRow: number;
	disableFooter?: boolean;
	onClick: (e: React.MouseEvent) => void;
};

export const GridItem = ({
	item,
	index,
	userLevel,
	animated,
	mobileRow,
	onClick,
	disableFooter,
}: Props) => {
	const t = useTranslations('Grid');

	const { clientId, tag, preCondition, videoLinks } = item;
	const playable = animated && !!videoLinks?.[0].downloadLinks?.[0].reference;
	const scrollplay = playable && !!mobileRow;

	const [viewRef, inView] = useInView({
		threshold: 0.25,
		skip: !scrollplay,
	});

	const videoRef = useRef<HTMLVideoElement>(null);

	const conditions = preCondition && formatBotPaymentOptions(preCondition);
	const requiredLevel = conditions?.options?.level?.value ?? 0;

	const totalMessagesCount = useFormatBotStatistics(item.statistics);
	const lockByLevel = requiredLevel > userLevel;

	const handlePlay = () => {
		if (!videoRef.current || !videoRef.current.paused) {
			return;
		}
		void videoRef.current.play();
	};
	const handleStop = () => {
		if (!videoRef.current || videoRef.current.paused) {
			return;
		}
		void videoRef.current.pause();
		videoRef.current.currentTime = 0;
	};

	const { ref } = useComputeActiveDistance({
		index,
		inView,
		mobileRow,
		scrollplay,
		onChange: (active) => (active ? handlePlay() : handleStop()),
	});

	const authorNick = item.authorProfile?.nick;

	return (
		<StyledGridItem>
			<Wrapper
				ref={ref}
				data-clientid={clientId}
				data-gridindex={index}
				data-requiredlevel={requiredLevel}
				href={lockByLevel ? routes.index : routes.chats.dialog(clientId)}
				prefetch={false}
				onClick={onClick}
				onMouseEnter={handlePlay}
				onMouseLeave={handleStop}
			>
				<Images ref={viewRef}>
					{playable ? (
						<Video
							ref={videoRef}
							poster={videoLinks[0].previewLinks[0].reference}
							preload="metadata"
							autoPlay={index === 0 && scrollplay}
							loop
							muted
							playsInline
							controls={false}
							disablePictureInPicture
							disableRemotePlayback
						>
							<source
								src={videoLinks[0].downloadLinks?.[0].reference}
								type="video/mp4"
							/>
						</Video>
					) : (
						<GridCarousel items={item.photoLinks} fallbacks={item.photoPaths} />
					)}

					<Tags>
						{lockByLevel && (
							<Tag size="XS" data-name="grid-item-level">
								<LockCircle />
								{t('gridItemLevel')} {requiredLevel}
							</Tag>
						)}
						{tag && tag.length > 0 && <Tag size="XS">{tag}</Tag>}
					</Tags>
					<TotalMessages>
						<SubHeadline size="S">{totalMessagesCount}</SubHeadline>
						<MessagesIcon width={18} height={18} />
					</TotalMessages>
				</Images>
				<Info>
					<Title size="M" align="left">
						{item.name}, {item.age}
					</Title>
					<Description color="grey-52-solid" size="M" align="left">
						{item.description}
					</Description>
					<MessagesButton
						bigIcon={true}
						leftIcon={<Messages />}
					></MessagesButton>
				</Info>
			</Wrapper>
			{!disableFooter && (
				<Footer>
					{item.accountType === 'influencer' ? (
						<>
							<TickSimple />
							<Text color="grey-68-solid" size="S">
								{t('gridItemDD')}
							</Text>
						</>
					) : item.accountType === 'user-generated' ? (
						<CreatorLink
							by={t('gridItemBy')}
							fallback={t('gridItemCommunityCharacter')}
							nick={authorNick}
						/>
					) : (
						<Text color="grey-68-solid" size="S">
							{t('gridItemOriginalCharacter')}
						</Text>
					)}
				</Footer>
			)}
		</StyledGridItem>
	);
};
